import React, { useRef, useState } from 'react';
import DynamicTableAnt from '../../hooks/DynamicTableAnt';
import Heading from '../../components/common/Heading/Heading';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  App,
  Button,
  Dropdown,
  Input,
  Popconfirm,
  Popover,
  Space,
  Tag,
} from 'antd';
import { queryKeys } from '../../services/react-query/queryKeys';
import { MdDelete, MdModeEditOutline } from 'react-icons/md';
import { EditOutlined, SearchOutlined } from '@ant-design/icons';
import {
  getJournalEntries,
  manageJournalEntry,
} from '../../network/journalEntry';
import { RiSendPlaneFill } from 'react-icons/ri';
import { TiCancel } from 'react-icons/ti';
import StatusTag from '../../hooks/StatusHook';
import { colorPrimary } from '../../utils/utils';
import { GiFiles } from 'react-icons/gi';
import { submitMaterialRequest } from '../../network/materialRequest';
import { PiDotsThreeVerticalBold } from 'react-icons/pi';
import DashboardCards from '../Dashboard/DashboardCards';

const JournalEntry = ({ t }) => {
  const client = useQueryClient();
  const { currentLang } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const searchInputcustomer = useRef(null);
  const searchInputMobilecustomer = useRef(null);
  const { message } = App.useApp();
  const [searchText, setSearchText] = useState('');
  const [searchKey, setSearchKey] = useState('');
  const [textValue, setTextValue] = useState('');
  const handleSearch = (selectedKeys, confirm, key) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchKey(key);
  };
  const handleReset = (clearFilters, confirm, setSelectedKeys) => {
    confirm();
    setSearchText('');
    setSearchKey('');
    if (clearFilters) {
      clearFilters();
    }
    if (setSelectedKeys) {
      setSelectedKeys(['']);
    }
  };
  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.getJournalEntries, searchKey, searchText, currentLang],
    queryFn: () => getJournalEntries(searchKey, searchText, currentLang),
    keepPreviousData: true,
  });
  const handleManageJournal = useMutation(
    // (id) => deletePatient(id),
    {
      mutationFn: ({ action: action, name: name }) =>
        manageJournalEntry({ action: action, name: name }),
      onSuccess: (data) => {
        // console.log(data, 'data');
        if (data?.message?.status === 500) {
          message.error(data?.message?.message);
          return false;
        }
        message.success(data?.message?.message);
        client.invalidateQueries({
          queryKey: [queryKeys.getJournalEntries],
        });
      },
      onError: () => {
        message.error('Something went wrong!');
      },
    }
  );
  const handleSubmitJournal = useMutation(
    // (id) => deletePatient(id),
    {
      mutationFn: (id) => submitMaterialRequest('Journal Entry', id),
      onSuccess: (data) => {
        // console.log(data, 'data');
        if (data?.message?.status === 500) {
          message.error(data?.message?.message);
          return false;
        }
        message.success(data?.message?.message);
        client.invalidateQueries({
          queryKey: [queryKeys.getJournalEntries],
        });
      },
      onError: () => {
        message.error('Something went wrong!');
      },
    }
  );

  const items = (record) => {
    return [
      // {
      //   key: 'edit',
      //   label: (
      //     <span className="flex items-center gap-2 text-[#818181] font-[700] text-[14px]">
      //       <EditOutlined />
      //       {t.edit} {t.customer}
      //     </span>
      //   ),
      //   onClick: () => navigate(`/customers/edit/${record?.name}`),
      // },
      // {
      //   key: 'address',
      //   label: (
      //     <span className="flex items-center gap-2 text-[#818181] font-[700] text-[14px]">
      //       <RiUserLocationFill />
      //       {t.addAddress}
      //     </span>
      //   ),
      //   onClick: () => navigate(`/address?open=true&customer=${record?.name}`),
      // },
      {
        key: 'editJournal',
        label: (
          <span className="flex items-center gap-2 font-[700] text-[14px]">
            <EditOutlined />
            {t.edit} {t.journal}
          </span>
        ),
        onClick: () => navigate(`/journal-entry/${record?.name}`),
      },
      {
        key: 'submitJournal',
        label: (
          <span className="flex items-center gap-2 font-[700] text-[14px]">
            <RiSendPlaneFill />
            {t.submitJournal}
          </span>
        ),
        disabled: record?.docstatus !== 0,
        onClick: () => handleSubmitJournal.mutate(record?.name),
      },

      {
        key: 'cancelJournal',
        label: (
          <span className="flex items-center gap-2 font-[700] text-[14px]">
            <TiCancel />
            {t.cancelJournal}
          </span>
        ),
        disabled: record?.docstatus !== 1,
        onClick: () =>
          handleManageJournal.mutate({
            action: 'cancel',
            name: record?.name,
          }),
      },
      {
        key: 'ledger',
        label: (
          <span className="flex items-center gap-2 font-[700] text-[14px]">
            <GiFiles />
            {t.GeneralLedger}
          </span>
        ),
        disabled: record?.docstatus !== 1,
        onClick: () =>
          navigate(`/general-ledger-report-journal/${record?.name}`),
      },
      {
        key: 'deleteJournal',
        label: (
          <Popconfirm
            title="Delete the payment entry"
            description="Are you sure to delete this payment entry?"
            onConfirm={() =>
              handleManageJournal.mutate({
                action: 'delete',
                name: record.name,
              })
            }
            okText="Yes"
            cancelText="No"
          >
            <span className="flex items-center gap-2 font-[700] text-[14px] text-red-600">
              <MdDelete />
              Delete Journal
            </span>
          </Popconfirm>
        ),
        disabled: record?.docstatus === 1,
      },
    ];
  };

  const columns = [
    {
      title: currentLang === 'en' ? 'ID' : 'الرقم',
      dataIndex: 'name',
      key: 'name',
      filterIcon: () => <SearchOutlined color={colorPrimary} />,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div
          style={{
            padding: 8,
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Input
            ref={searchInputMobilecustomer}
            placeholder={currentLang === 'en' ? 'ID' : 'الرقم'}
            value={textValue}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
              setTextValue(e.target.value);
            }}
            onPressEnter={() => handleSearch(selectedKeys, confirm, 'name')}
            style={{
              marginBottom: 8,
              display: 'block',
            }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys, confirm, 'name');
                setSearchKey('name');
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{
                width: 90,
              }}
            >
              {t.Search}
            </Button>
            <Button
              onClick={() => clearFilters && handleReset(clearFilters, confirm)}
              size="small"
              style={{
                width: 90,
              }}
            >
              {t.Reset}
            </Button>
          </Space>
        </div>
      ),
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTextValue('');
          setSearchText('');
          setSearchKey('');
          setTimeout(() => searchInputcustomer.current?.select(), 100);
        }
      },
      render: (text, record) => {
        return (
          <span
            style={{ cursor: 'pointer' }}
            className="text-primary"
            onClick={() => {
              navigate(`/journal-entry/${record.name}`);
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: currentLang === 'en' ? 'Title' : 'اسم الحزب',
      dataIndex: 'title',
      key: 'title',
      filteredValue: [searchText],
      onFilter: (value, record) => {
        const title = String(record.title).toLowerCase();
        const name = String(record.name).toLowerCase();
        const searchValue = value.toLowerCase();
        return name.includes(searchValue) || title.includes(searchValue);
      },
    },
    {
      title: currentLang === 'en' ? 'Status' : 'نوع الدفع',
      dataIndex: 'voucher_type',
    },
    {
      title: currentLang === 'en' ? 'Total Debit' : 'طريقة الدفع',
      dataIndex: 'total_debit',
    },
    {
      title: currentLang === 'en' ? 'Total Credit' : 'إجمالي الائتمان',
      dataIndex: 'total_credit',
    },
    // {
    //   title: currentLang === 'en' ? 'Reference Number' : 'تاريخ النشر',
    //   dataIndex: 'cheque_no',
    // },
    {
      title: currentLang === 'en' ? 'Status' : 'الحالة',
      dataIndex: 'docstatus',
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: t.action,
      dataIndex: 'action',
      key: 'action',
      render: (text, record) => {
        // console.log('record:', record);
        return (
          // <Space>
          //   <Popover
          //     placement="top"
          //     title={t.edit}
          //     onClick={() => navigate(`/journal-entry/${record?.name}`)}
          //   >
          //     <Button
          //       size="small"
          //       type="primary"
          //       // onClick={() => navigate(`/patients/${record.uid}`)}
          //     >
          //       <MdModeEditOutline />
          //     </Button>
          //   </Popover>

          //   {record.docstatus === 0 ? (
          //     <Popover
          //       placement="top"
          //       title={t.submitJournal}
          //       onClick={() => {
          //         // console.log('record:', record?.name);
          //         // handleManageJournal.mutate({
          //         //   action: 'submit',
          //         //   name: record.name,
          //         // });
          //         handleSubmitJournal.mutate(record?.name);
          //       }}
          //     >
          //       <Button size="small" type="primary">
          //         <RiSendPlaneFill />
          //       </Button>
          //     </Popover>
          //   ) : record.docstatus === 1 ? (
          //     <Popover
          //       placement="top"
          //       title={t.cancelJournal}
          //       onClick={() => {
          //         // console.log('record:', record?.name);
          //         handleManageJournal.mutate({
          //           action: 'cancel',
          //           name: record.name,
          //         });
          //       }}
          //     >
          //       <Button size="small" type="primary" danger>
          //         <TiCancel />
          //       </Button>
          //     </Popover>
          //   ) : null}
          //   {record?.docstatus === 1 && (
          //     <Popover
          //       placement="top"
          //       title={t.GeneralLedger}
          //       onClick={() => {
          //         navigate(`/general-ledger-report-journal/${record?.name}`);
          //       }}
          //     >
          //       <Button
          //         size="small"
          //         type="primary"
          //         icon={<GiFiles />}
          //         style={{
          //           backgroundColor: '#c12f6e',
          //         }}
          //       />
          //     </Popover>
          //   )}
          //   {record.docstatus !== 1 && (
          //     <Popconfirm
          //       title="Delete the payment entry"
          //       description="Are you sure to delete this payment entry?"
          //       onConfirm={() =>
          //         handleManageJournal.mutate({
          //           action: 'delete',
          //           name: record.name,
          //         })
          //       }
          //       // onCancel={cancel}
          //       okText="Yes"
          //       cancelText="No"
          //     >
          //       <Button size="small" type="primary" danger>
          //         <MdDelete />
          //       </Button>
          //     </Popconfirm>
          //   )}
          // </Space>
          <Dropdown
            menu={{ items: items(record) }}
            overlayClassName="custom-dropdown"
            trigger={['click']}
          >
            <Button
              type="text"
              icon={<PiDotsThreeVerticalBold size={20} color="#86888C" />}
            />
          </Dropdown>
        );
      },
    },
  ];
  const dataTable = data?.data?.map((item, index) => {
    return {
      ...item,
      key: index,
    };
  });
  return (
    <div className="journal-entry">
      {/* <Heading title={t.Finance} subTitle={t.JournalEntry} path="/" /> */}
      <DashboardCards t={t} />
      <div className="mt-5">
        <DynamicTableAnt
          columns={columns}
          data={dataTable}
          loading={isLoading}
          setSearchText={setSearchText}
          titleBtn={t.addJournalEntry}
          Placeholder={t.searchJournalEntry + ' ' + t.or + ' ' + t.id}
          pathBtn="/journal-entry/add"
        />
      </div>
    </div>
  );
};

export default JournalEntry;
